/**
 * ops 依頼の SLA 算出。カテゴリ × 緊急度で目標営業日を決め、週末を除いた営業日経過で残り日数を出す。
 * 0 日目は「起票した時点以降の最初の平日」で、19 時（JST）以降の起票は翌日の起票として数える（夜間・週末の
 * 起票が、誰も対応できない時間に目標を消費しないようにする）。祝日は未対応（Phase 3 で祝日カレンダーに寄せる stopgap）。frontend の SLA バッジ・SLO 集計、
 * および将来の backend 集計が同じ定義を使うため、**SLO 閾値の変更はこのファイル一箇所**で完結する。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business ops 依頼の SLA（目標営業日・残日数・逼迫状態・SLO 達成）と起票→完了の処理営業日数を算出する共有ロジック。0 日目は起票時点以降の最初の平日（19 時以降の起票は翌日扱い）。SLO の閾値と営業日の数え方はここが単一の source of truth
 * @graph-connects none
 */
import { getJstYmd } from '@cortex/jst-calendar';
import { JST_OFFSET_MS } from './jst.js';
import { isUrgentUrgency, OPS_SELECTABLE_CATEGORIES } from './status.js';
/** 1 時間のミリ秒（営業時間の計算で共有）。 @graph-connects none */
const HOUR_MS = 3_600_000;
/** 1 日のミリ秒（営業日・営業時間の計算で共有）。 @graph-connects none */
const DAY_MS = 24 * HOUR_MS;
/**
 * 週末（土日）を除いた営業日数を、JST 暦日ペアから数える（start 当日を 0 日目とし、start < d <= end の
 * 平日を数える）。`Date.UTC` 基準の純粋な暦計算でプロセス TZ に依存しない。ops-core（frontend の SLA/SLO）
 * と customer-success MCP の SLA 判定が同じ営業日計算を共有するための単一 source。呼び出し側は必ず
 * `getJstYmd` で JST 暦日へ正規化してから渡すこと（生の Date を渡さない）。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business ops SLA の営業日経過を JST 暦日ペアから算出する共有関数。週末を除外し祝日は未対応
 * @graph-connects none
 */
export function businessDaysBetweenJst(start, end) {
    const startUtc = Date.UTC(start.year, start.month - 1, start.day);
    const endUtc = Date.UTC(end.year, end.month - 1, end.day);
    const totalDays = Math.floor((endUtc - startUtc) / DAY_MS);
    if (totalDays <= 0)
        return 0;
    const startDow = new Date(startUtc).getUTCDay(); // 0=Sun … 6=Sat（Date.UTC 由来なので TZ 非依存）
    let count = Math.floor(totalDays / 7) * 5;
    for (let i = 1; i <= totalDays % 7; i += 1) {
        const dow = (startDow + i) % 7;
        if (dow !== 0 && dow !== 6)
            count += 1;
    }
    return count;
}
/**
 * この時刻（JST、時）以降の起票は翌日の起票として数える。夜に起票された依頼の SLA を、対応できない夜の
 * うちから消費させない（例: 即時の依頼を 20 時に起票すると、翌朝には既に超過になっていた）。
 * @graph-connects none
 */
export const OPS_SLA_EVENING_CUTOFF_HOUR_JST = 19;
/**
 * SLA の 0 日目（JST 暦日）を返す。19 時（JST）以降の起票は翌日扱いにし、そこが土日なら次の月曜にする
 * （金曜夜・土日の起票はどちらも月曜が 0 日目）。SLA バッジ・SLO 判定・処理日数が同じ 0 日目を使う単一 source。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business ops SLA の 0 日目（起票時点以降の最初の平日。19 時以降の起票は翌日扱い）を JST 暦日で返す
 * @graph-connects none
 */
export function opsSlaStartDayJst(submittedAt) {
    const jst = new Date(submittedAt.getTime() + JST_OFFSET_MS);
    let dayUtc = Date.UTC(jst.getUTCFullYear(), jst.getUTCMonth(), jst.getUTCDate());
    if (jst.getUTCHours() >= OPS_SLA_EVENING_CUTOFF_HOUR_JST)
        dayUtc += DAY_MS;
    const dow = new Date(dayUtc).getUTCDay(); // 0=Sun … 6=Sat（Date.UTC 由来なので TZ 非依存）
    if (dow === 6)
        dayUtc += 2 * DAY_MS;
    if (dow === 0)
        dayUtc += DAY_MS;
    const day = new Date(dayUtc);
    return { year: day.getUTCFullYear(), month: day.getUTCMonth() + 1, day: day.getUTCDate() };
}
/**
 * 起票から end までの SLA 上の経過営業日数。0 日目は `opsSlaStartDayJst`、以降は土日を除いて数える。
 * end が 0 日目より前（夜・週末の起票の直後）なら 0。
 * @graph-connects opsSlaStartDayJst [calls] 0 日目（19 時以降・土日の起票は次の平日）
 * @graph-connects businessDaysBetweenJst [calls] JST 暦日・土日除外の営業日数
 */
function slaElapsedBusinessDays(submittedAt, end) {
    return businessDaysBetweenJst(opsSlaStartDayJst(submittedAt), getJstYmd(end));
}
/**
 * 起票から完了までの営業日数（SLA と同じ数え方: JST 暦日・土日除外・祝日未考慮・19 時以降と土日の起票は次の平日が 0 日目）。未完了・日時が壊れている
 * 依頼は null（「0 日で完了」と混ぜない）。SLA 対象外の種別（legacy シートの未知の種別）でも数える（処理日数は SLA 目標と独立）。
 * ダッシュボードのメンバー別平均処理日数の単一 source。
 * @graph-connects slaElapsedBusinessDays [calls] SLA と同じ 0 日目からの営業日数を数える
 */
export function opsProcessingBusinessDays(submittedAt, completedAt) {
    const start = parseOpsTimestamp(submittedAt);
    const end = parseOpsTimestamp(completedAt);
    if (start === null || end === null)
        return null;
    return slaElapsedBusinessDays(start, end);
}
/**
 * 2 時刻の間の営業日数（JST 暦日・土日除外・祝日未考慮。start の暦日を 0 日目にし、19 時の繰り越しは
 * しない）。SLA の 0 日目ルール（`opsSlaStartDayJst`）は「誰も対応できない時間の起票」のためのもので、
 * 差戻しから再開までのような起票以外の経過には当てはめない。未設定・壊れた日時は null。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business 2 時刻の間の営業日数（JST 暦日・土日除外、起点の暦日が 0 日目）。差戻しから再開までなど起票以外の経過に使う
 * @graph-connects parseOpsTimestamp [calls] 日時の解釈（未設定・壊れた値は null）
 * @graph-connects businessDaysBetweenJst [calls] JST 暦日・土日除外の営業日数
 */
export function opsCalendarBusinessDays(start, end) {
    const s = parseOpsTimestamp(start);
    const e = parseOpsTimestamp(end);
    if (s === null || e === null)
        return null;
    return businessDaysBetweenJst(getJstYmd(s), getJstYmd(e));
}
/**
 * 2 時刻の間の経過時間（時間単位）から JST の土日にかかる部分を除いたもの。営業日（`businessDaysBetweenJst`）と
 * 同じく土日のみ除外・祝日は未考慮で、同日中の短い経過（数時間）も 0 に潰さずに測れる。
 * 差戻しから調査再開までの時間のように「営業日では粗すぎる」経過に使う。end <= start は 0。
 * @graph-connects none
 */
export function opsBusinessHoursBetween(start, end) {
    const startMs = start.getTime();
    const endMs = end.getTime();
    if (!(endMs > startMs))
        return 0;
    let businessMs = 0;
    // JST の暦日ごとに区切り、平日の日にかかる部分だけを足す（DST が無いので日境界は固定オフセットで決まる）。
    const firstJstMidnight = Math.floor((startMs + JST_OFFSET_MS) / DAY_MS) * DAY_MS - JST_OFFSET_MS;
    for (let dayStart = firstJstMidnight; dayStart < endMs; dayStart += DAY_MS) {
        const dow = new Date(dayStart + JST_OFFSET_MS).getUTCDay();
        if (dow === 0 || dow === 6)
            continue;
        businessMs += Math.min(endMs, dayStart + DAY_MS) - Math.max(startMs, dayStart);
    }
    return businessMs / HOUR_MS;
}
/**
 * 依頼の日時（ISO）を Date にする。未設定（null / undefined / 空文字）や壊れた値は null。完了日時の
 * 「未完了」判定と処理日数・SLO 判定が同じ解釈を使うための単一の入口。
 * @graph-connects none
 */
function parseOpsTimestamp(value) {
    if (value === undefined || value === null || value === '')
        return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}
/**
 * 種別 × 緊急かどうかから目標営業日を返す。SLA 対象外は null。**SLO 閾値そのもの**で、
 * SLA バッジ・SLO 集計・画面の説明表がすべてこの 1 つの関数を見る。
 *
 * 画面に閾値を出すときは数値をベタ書きせず必ずここから引くこと。ベタ書きすると
 * 閾値を変えた時に説明だけ古い値のまま残り、画面が嘘をつく（しかも誰も気付けない）。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business ops SLA の目標営業日を種別 × 緊急度から返す。調査依頼 7（緊急 3）/ 状態変更・データ修正・SQL レビュー 3（緊急 0）/ Fatal 0、未知の種別は SLA 対象外
 * @graph-connects none
 */
export function opsSloTargetDays(category, urgent) {
    switch (category) {
        case '調査依頼':
            return urgent ? 3 : 7;
        case '状態変更':
        case 'データ修正':
        case 'SQLレビュー':
            return urgent ? 0 : 3;
        case 'Fatal':
            return 0;
        default:
            return null;
    }
}
/**
 * 画面の SLO 説明表に出す行。`opsSloTargetDays` から**導出**するので、閾値を変えれば表も一緒に動く。
 * 起票できる種別（`OPS_SELECTABLE_CATEGORIES`）だけを載せる。Fatal は別シート運用で
 * このダッシュボードからは起票も種別変更もできないため、説明しても選べず混乱するだけ。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business 起票できる種別ごとの SLO 目標営業日（通常 / 緊急）の一覧。画面の SLO 説明表がそのまま描画する
 * @graph-connects opsSloTargetDays [calls] 各行の目標営業日を閾値の SoT から導出
 * @graph-connects OPS_SELECTABLE_CATEGORIES [references] 行に並べる種別（起票できる種別）の一覧
 */
export const OPS_SLO_TARGET_ROWS = OPS_SELECTABLE_CATEGORIES.flatMap((category) => {
    const normalDays = opsSloTargetDays(category, false);
    const urgentDays = opsSloTargetDays(category, true);
    // SLA 対象外の種別は目標が無いので行にしない（出しても '—' しか書けず、表示側に
    // 到達しない null 分岐を作るだけになる）。現状の 4 種別はすべて目標を持つ。
    return normalDays === null || urgentDays === null ? [] : [{ category, normalDays, urgentDays }];
});
/**
 * 依頼の種別 × 緊急度から目標営業日を返す。
 * @graph-connects opsSloTargetDays [calls] 閾値の SoT に委譲
 * @graph-connects isUrgentUrgency [calls] 緊急度が緊急（即時 / 当日）かを判定
 */
function targetDaysFor(input) {
    return opsSloTargetDays(input.category, isUrgentUrgency(input.urgency));
}
/**
 * 依頼の SLA を算出する。クローズ済み・SLA 対象外は state='none'。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business 起票日時と目標営業日から SLA バッジ / SLO 集計用の状態・残り日数を返す（19 時以降・土日の起票は次の平日が 0 日目）
 * @graph-connects slaElapsedBusinessDays [calls] 0 日目からの経過営業日
 */
export function computeOpsSla(input, now = new Date()) {
    const target = targetDaysFor(input);
    if (input.state === 'closed' || target === null) {
        return {
            state: 'none',
            label: '—',
            status: { kind: 'none', days: 0 },
            elapsedDays: 0,
            targetDays: target,
        };
    }
    const elapsed = slaElapsedBusinessDays(new Date(input.submittedAt), now);
    const remaining = target - elapsed;
    if (remaining < 0) {
        return {
            state: 'breach',
            label: `${String(-remaining)}d over`,
            status: { kind: 'over', days: -remaining },
            elapsedDays: elapsed,
            targetDays: target,
        };
    }
    if (remaining === 0) {
        return {
            state: 'breach',
            label: 'due today',
            status: { kind: 'dueToday', days: 0 },
            elapsedDays: elapsed,
            targetDays: target,
        };
    }
    const state = remaining <= 2 ? 'warn' : 'ok';
    return {
        state,
        label: `${String(remaining)}d left`,
        status: { kind: 'left', days: remaining },
        elapsedDays: elapsed,
        targetDays: target,
    };
}
/**
 * 依頼が SLA 目標を満たしたかを判定する（SLO 達成率の集計単位）。computeOpsSla が「今この瞬間の逼迫
 * （未クローズ向けのライブ表示）」を返すのに対し、これは **解決済みは解決時点まで、未解決は現時点まで** の
 * 営業日経過で met / missed を返す。そのため過去期間（大半が解決済み）でも 0% に潰れず、実際に目標内で
 * 解決できた割合を返す。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business 依頼が SLA 目標営業日内に解決した（未解決なら現時点でまだ目標内）かを返す。SLO 達成率の集計に使う
 * @graph-connects slaElapsedBusinessDays [calls] 0 日目からの経過営業日
 */
export function computeOpsSloOutcome(input, now = new Date()) {
    const target = targetDaysFor(input);
    if (target === null) {
        return { eligible: false, metTarget: false, elapsedDays: 0, targetDays: null };
    }
    // 完了日時が無い / 壊れている依頼は未解決として現時点までの経過で判定する（Invalid Date で営業日を数えない）。
    const end = parseOpsTimestamp(input.completedAt) ?? now;
    const elapsed = slaElapsedBusinessDays(new Date(input.submittedAt), end);
    return { eligible: true, metTarget: elapsed <= target, elapsedDays: elapsed, targetDays: target };
}
/**
 * SLO 達成率の目標ライン（%）。達成率がこの値以上なら「目標達成」。ダッシュボードの色分け（達成/未達）と
 * 目標差分（±Npt）表示が参照する単一 source。SLO 目標の変更はここ一箇所で完結する。
 * @graph-connects none
 */
export const OPS_SLO_TARGET_PERCENT = 90;
//# sourceMappingURL=sla.js.map